import { makeAutoObservable, runInAction } from 'mobx';
import { whitelistApi } from '@/services/whitelistApi';
import type {
  AddUserToWhitelistRequest,
  Whitelist,
  WhitelistItem,
} from '@/types';

// 字段转换函数：将 API 返回的 snake_case 格式转换为前端使用的 camelCase 格式
// created_at 是秒级时间戳（int64），直接传递即可
function transformWhitelist(apiWhitelist: Whitelist): WhitelistItem {
  return {
    id: apiWhitelist.id,
    userId: apiWhitelist.user_id,
    createdAt: apiWhitelist.created_at,
  };
}

class WhitelistStore {
  whitelists: WhitelistItem[] = [];
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  searchUserId = '';
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // 获取白名单列表
  async fetchWhitelist() {
    this.loading = true;
    try {
      const response = await whitelistApi.listWhitelist({
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize,
        user_id: this.searchUserId || undefined,
      });
      runInAction(() => {
        this.whitelists = response.whitelists.map(transformWhitelist);
        this.pagination.total = response.total;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 添加用户到白名单
  async addUserToWhitelist(data: AddUserToWhitelistRequest) {
    this.loading = true;
    try {
      await whitelistApi.addUserToWhitelist(data);
      // 添加后重新获取列表
      await this.fetchWhitelist();
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 移除用户从白名单
  async removeUserFromWhitelist(userId: string) {
    this.loading = true;
    try {
      await whitelistApi.removeUserFromWhitelist({ user_id: userId });
      // 删除后重新获取列表
      await this.fetchWhitelist();
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  // 设置搜索用户ID
  setSearchUserId(userId: string) {
    this.searchUserId = userId;
    this.pagination.page = 1; // 重置到第一页
  }

  // 设置分页
  setPagination(page: number, pageSize?: number) {
    this.pagination.page = page;
    if (pageSize) {
      this.pagination.pageSize = pageSize;
    }
  }
}

export const whitelistStore = new WhitelistStore();
